import {
  defineStyleConfig,
  extendTheme,
  theme as baseTheme,
  ThemeComponents,
  ThemeConfig,
  withDefaultColorScheme,
} from '@chakra-ui/react';
import { CalendarDefaultTheme } from '@uselessdev/datepicker';
import { StepsStyleConfig as Steps } from 'chakra-ui-steps';

const config: ThemeConfig = {
  useSystemColorMode: false,
  initialColorMode: 'light',
};

const components: ThemeComponents = {
  Button: defineStyleConfig({
    baseStyle: {
      fontWeight: 'normal',
      textTransform: 'capitalize',
    },
    defaultProps: {
      size: 'md',
      variant: 'solid',
    },
  }),
  FormLabel: defineStyleConfig({
    baseStyle: {
      fontWeight: 'normal',
    },
  }),
};

const colors = {
  gray: {
    50: '#F2F2F3',
    100: '#DADADD',
    200: '#C2C2C7',
    300: '#AAAAB1',
    400: '#92929B',
    500: '#8C8C95',
    600: '#62626A',
    700: '#494950',
    800: '#313135',
    900: '#18181B',
  },
  black: {
    50: '#F2F2F2',
    100: '#DBDBDB',
    200: '#C4C4C4',
    300: '#ADADAD',
    400: '#969696',
    500: '#000000',
    600: '#666666',
    700: '#4D4D4D',
    800: '#333333',
    900: '#1A1A1A',
  },
  blue: {
    50: '#E6F0FE',
    100: '#BAD5FC',
    200: '#8EBBFB',
    300: '#62A0F9',
    400: '#3685F7',
    500: '#053984',
    600: '#0755C5',
    700: '#064093',
    800: '#042A62',
    900: '#021531',
  },
  darkBlue: {
    50: '#EFF2F5',
    100: '#D3DAE3',
    200: '#B7C2D1',
    300: '#9BAABF',
    400: '#7F92AE',
    500: '#4C5D77',
    600: '#50617C',
    700: '#3C495D',
    800: '#28313E',
    900: '#14181F',
  },
  orange: {
    50: '#FEF1E7',
    100: '#FBD9BB',
    200: '#F9C090',
    300: '#F7A764',
    400: '#F58F38',
    500: '#F4892E',
    600: '#C25F0A',
    700: '#914708',
    800: '#612F05',
    900: '#301803',
  },
  green: {
    50: '#E5FFF1',
    100: '#B8FFD8',
    200: '#8AFFC0',
    300: '#5CFFA7',
    400: '#2EFF8E',
    500: '#00AC4F',
    600: '#00CC5E',
    700: '#009946',
    800: '#00662F',
    900: '#003317',
  },
  lightBlue: {
    50: '#E8F4FD',
    100: '#BDE1F9',
    200: '#93CDF6',
    300: '#69B9F2',
    400: '#3EA6EE',
    500: '#50AEF0',
    600: '#1075BC',
    700: '#0C588D',
    800: '#083B5E',
    900: '#041D2F',
  },
};

const theme = extendTheme(
  CalendarDefaultTheme,
  {
    config,
    components: {
      ...components,
      Calendar: {
        parts: ['calendar', 'months'],
        baseStyle: {
          calendar: {
            py: '10px',
            bg: 'white',
            rounded: '2xl',
            borderWidth: '.5px',
            shadow: 'md',
            color: 'black',
            maxW: 'full',
          },
          months: {
            px: [0, 4],
            w: 'full',
          },
        },
      },

      CalendarDay: {
        baseStyle: {
          color: '#4A5660',
          fontWeight: 'normal',
          fontSize: 'xs',
          rounded: 'lg',
          w: 'full',
          h: 'full',
          '&[aria-current="date"]': {
            bg: '#40B8EA',
          },
          _active: {
            bg: 'gray.100',
          },
          _hover: {
            bg: 'gray.50',
          },
        },
      },

      CalendarMonth: {
        parts: ['month', 'name', 'week', 'weekday'],
        baseStyle: {
          month: {
            w: 'full',
          },
          name: {
            fontWeight: 'normal',
            rounded: 'full',
            fontSize: 'md',
            color: 'black',
            bgColor: 'white',
          },

          week: {
            my: '20px',
            W: 'full',
          },

          weekday: {
            textTransform: 'uppercase',
            color: '#B5BEC6',
            fontSize: '12px',
          },
        },
      },

      CalendarControl: {
        parts: ['button', 'controls'],

        baseStyle: {
          button: {
            rounded: 'sm',
            fontSize: 'lg',
            color: '#B5BEC6',
            bgColor: 'white',
            _hover: {
              bgColor: 'white',
            },
            _active: {
              bg: 'white',
            },
            _focus: {
              outline: 'none',
            },
          },
        },
      },
      Steps: {
        ...Steps,
        // baseStyle: (props: StyleFunctionProps) => ({
        //   ...Steps.baseStyle(props),
        // }),
      },
    },
    colors,
    fonts: {
      heading: `Jost, ${baseTheme.fonts.heading}`,
      body: `Jost, ${baseTheme.fonts.body}`,
    },
  },
  withDefaultColorScheme({
    colorScheme: 'blue',
  })
);

export default theme;
